import { useBrowserLocation } from "@folio-2026/browser-location"
import Experience from "@folio-2026/experience"
import { LoadResult } from "@folio-2026/resources"
import { circleMask, gridPattern, plusPattern } from "@folio-2026/shared"
import AnimatedModel from "@folio-2026/shared/animatedmodel"
import assert from "assert"
import { SkeletonUtils } from "three/examples/jsm/Addons.js"
import {
  add,
  cameraPosition,
  cameraProjectionMatrix,
  cameraViewMatrix,
  color,
  div,
  dot,
  float,
  Fn,
  length,
  max,
  mul,
  normalize,
  normalWorld,
  positionWorld,
  pow,
  refract,
  smoothstep,
  step,
  texture,
  uniform,
  uv,
  vec2,
  vec3,
  vec4,
} from "three/tsl"
import * as THREE from "three/webgpu"
import { FolderApi } from "tweakpane"

import { useLoadingStore } from "../store"
import OuterParticles from "./OuterParticles"

export default class Man {
  experience: Experience
  sizes: Experience["sizes"]
  pointer: Experience["pointer"]
  envMap: THREE.Texture
  loadingScene: THREE.Texture
  perlin: THREE.Texture
  fbm: THREE.Texture
  debugFolder?: FolderApi
  animatedModel!: AnimatedModel
  model: THREE.Object3D
  outerParticles!: OuterParticles
  shadowMesh?: THREE.Mesh

  isMouseOver = true // Default to true since renderer likely covers viewport
  headUpLoopListenerBound:
    | ((event: THREE.AnimationMixerEventMap["finished"]) => void)
    | null = null
  aboutPageTimeoutId: ReturnType<typeof setTimeout> | null = null
  aboutPageIdleTimeoutId: ReturnType<typeof setTimeout> | null = null

  mouseCoordsNDC = uniform(vec2(0.5))
  circleActive = uniform(0)
  circleRadiusNDC = uniform(0.5)
  fadeOutMultiplier = uniform(1.0)
  circleTrailPositions: THREE.UniformNode<THREE.Vector2>[] = []
  circleTrailLength = 10
  trailUpdateCounter = 0
  trailUpdateInterval = 3 // Update trail every N frames

  plusPatternEnabled = uniform(window.location.pathname === "/about" ? 1 : 0)
  plusPatternColor = uniform(color("#98c5ff"))
  plusPatternSpacing = uniform(0.01)
  plusPatternSize = uniform(0.1)
  plusPatternVisibility = uniform(0.1)
  plusPatternBlinkSpeed = uniform(2.0)
  shadowColor = uniform(color("#02080e"))
  gridColor = uniform(color("#24104e"))
  gridSize = uniform(0.5)

  shadowOpacity = uniform(0.35)
  shadowSettings = {
    radius: 5,
    opacity: 0.9,
    yOffset: 0.003,
  }

  settings = {
    position: {
      x: -0.4,
      z: 0,
    },
    rotation: {
      y: 0,
    },
    light: {
      fresnelMultiplier: uniform(0.18),
      fresnelFadeHeight: uniform(0.6),
    },
  }

  constructor({
    scene,
    debugFolder,
    resources,
  }: {
    scene: THREE.Object3D
    debugFolder?: FolderApi
    resources: {
      [key: string]: LoadResult
    }
  }) {
    this.experience = Experience.getInstance()
    this.sizes = this.experience.sizes
    this.pointer = this.experience.pointer

    this.loadingScene = resources["loading-scene"] as THREE.Texture
    this.perlin = this.experience.globalResources.perlin as THREE.Texture
    this.perlin.wrapS = THREE.RepeatWrapping
    this.perlin.wrapT = THREE.RepeatWrapping

    this.fbm = this.experience.globalResources.fbm as THREE.Texture
    this.fbm.wrapS = THREE.RepeatWrapping
    this.fbm.wrapT = THREE.RepeatWrapping

    this.envMap = this.loadingScene

    this.debugFolder = debugFolder

    const manResource = resources["man"]
    assert(manResource, "Man resource not found")
    assert(manResource.scene, "Man resource scene not found")

    this.model = SkeletonUtils.clone(manResource.scene as THREE.Group)
    assert(this.model, "Model not found")

    // Initialize trail positions
    this.circleTrailPositions = Array.from(
      { length: this.circleTrailLength },
      () => uniform(vec2(0.1)),
    )

    const animations = manResource.animations || []

    this.animatedModel = new AnimatedModel({
      source: this.model,
      animations,
      container: scene as THREE.Scene,
      debugFolder: this.debugFolder,
    })

    this.setMaterial()

    if (this.model) {
      this.model.position.z = this.settings.position.z
      this.model.position.x = this.settings.position.x
    }

    this.createFakeShadow({
      scene: scene as THREE.Scene,
    })

    const pathname = useBrowserLocation.getState().pathname
    if (pathname === "/about") {
      // Start intro, but ensure idle is also playing in background
      const introAction = this.animatedModel.findAction("intro")
      const idleAction = this.animatedModel.findAction("idle")

      if (introAction && idleAction) {
        // Start idle first to ensure it's playing in background
        this.startIdlePosition()
        // Keep idle playing with weight 0 in background to preserve its time
        idleAction.setEffectiveWeight(0)

        // Use fadeToAction to properly initialize and start intro
        // Since this is initial load, there's no previous action to fade from
        introAction.reset()
        introAction.setEffectiveTimeScale(1)
        introAction.setEffectiveWeight(1)
        if (!introAction.isRunning()) {
          introAction.play()
        }
        this.animatedModel.lastActivatedAction = introAction
      } else {
        this.animatedModel.startAction("intro")
      }
    } else {
      this.startIdlePosition()
    }

    // Get the SkinnedMesh directly to ensure same reference
    let skinnedMesh: THREE.SkinnedMesh | null = null
    this.model.traverse((child) => {
      if (child instanceof THREE.SkinnedMesh && !skinnedMesh) {
        skinnedMesh = child
      }
    })
    assert(skinnedMesh, "SkinnedMesh not found in model")

    this.outerParticles = new OuterParticles({
      scene,
      man: skinnedMesh,
      debugFolder: this.debugFolder,
      fbm: this.fbm,
      perlin: this.perlin,
    })

    this.setDebug()
  }

  private createFakeShadow({ scene }: { scene: THREE.Scene }) {
    if (this.shadowMesh) return

    this.shadowOpacity.value = this.shadowSettings.opacity

    // Size the blob from the model bounds (fallback to defaults)
    this.model.updateMatrixWorld(true)
    const box = new THREE.Box3().setFromObject(this.model)
    const size = new THREE.Vector3()
    box.getSize(size)

    const computedRadius = Math.max(0.6, Math.max(size.x, size.z) * 0.8)
    this.shadowSettings.radius = computedRadius

    const geometry = new THREE.PlaneGeometry(1, 1)
    const material = new THREE.MeshBasicNodeMaterial({ transparent: true })
    material.depthWrite = false

    // Radial falloff from plane UVs (0..1)
    const centeredUv = uv().sub(vec2(0.5))
    const dist = length(centeredUv)
    const radialMask = smoothstep(0, 0.5, dist).oneMinus()
    material.opacityNode = radialMask.mul(radialMask).mul(this.shadowOpacity)
    material.colorNode = this.shadowColor

    const mesh = new THREE.Mesh(geometry, material)
    mesh.name = "ManFakeShadow"
    mesh.rotation.x = -Math.PI / 2
    mesh.position.y = this.shadowSettings.yOffset
    mesh.scale.set(5, 5, 1)
    mesh.renderOrder = 1

    this.shadowMesh = mesh
    scene.add(mesh)
  }

  setMaterial() {
    if (!this.model) {
      return
    }

    const material = new THREE.MeshBasicNodeMaterial()
    material.side = THREE.FrontSide

    const viewDir = normalize(positionWorld.sub(cameraPosition))
    const inverseFresnel = max(0, dot(viewDir.mul(-1), normalWorld))
    const fresnel = inverseFresnel.oneMinus()

    // Pre-compute shared values outside the function to avoid redundant calculations
    const worldPos4 = vec4(positionWorld, 1)
    const viewPos4 = cameraViewMatrix.mul(worldPos4)
    const clipPos4 = cameraProjectionMatrix.mul(viewPos4)
    const fragScreen = vec2(
      clipPos4.x.div(clipPos4.w),
      clipPos4.y.div(clipPos4.w),
    )

    const colorShader = Fn(() => {
      const baseColor = vec3(1)
      const light = vec3(0, 0.003, 0.005)

      const refFresnel = pow(fresnel, 1)
      const refractDirR = normalize(refract(viewDir, normalWorld, div(1, 1.07)))
      const refractDirG = normalize(
        refract(viewDir, normalWorld, div(1, add(1.07, mul(0.2, refFresnel)))),
      )
      const refractDirB = normalize(
        refract(viewDir, normalWorld, div(1, add(1.07, mul(0.6, refFresnel)))),
      )
      const refractColorR = texture(this.envMap, refractDirR).r
      const refractColorG = texture(this.envMap, refractDirG).g
      const refractColorB = texture(this.envMap, refractDirB).b
      const refractColor = vec3(refractColorR, refractColorG, refractColorB)

      // Height-based fresnel fade: fade from 0 at ground (Y=0) to full value at fadeHeight
      const fadeHeight = this.settings.light.fresnelFadeHeight
      const worldY = positionWorld.y
      // Create smooth fade: 0 at Y=0, 1 at Y=fadeHeight and above
      const heightFadeFactor = smoothstep(float(0), fadeHeight, worldY)
      // Apply fade to fresnel multiplier
      const fadedFresnelMultiplier =
        this.settings.light.fresnelMultiplier.mul(heightFadeFactor)

      light.addAssign(refractColor.mul(fresnel).mul(fadedFresnelMultiplier))

      const frColor = baseColor.mul(light)

      // Current circle (fadeOutMultiplier is already applied via circleActive)
      const currentCircleMask = circleMask({
        coordsNDC: this.mouseCoordsNDC,
        radiusNDC: this.circleRadiusNDC,
      }).mul(this.circleActive)

      // Trail circles with fading opacity (manually unrolled for shader)
      const trail0 = circleMask({
        coordsNDC: this.circleTrailPositions[0],
        radiusNDC: this.circleRadiusNDC,
      }).mul(0.5)
      const trail1 = circleMask({
        coordsNDC: this.circleTrailPositions[1],
        radiusNDC: this.circleRadiusNDC,
      }).mul(0.45)
      const trail2 = circleMask({
        coordsNDC: this.circleTrailPositions[2],
        radiusNDC: this.circleRadiusNDC,
      }).mul(0.4)
      const trail3 = circleMask({
        coordsNDC: this.circleTrailPositions[3],
        radiusNDC: this.circleRadiusNDC,
      }).mul(0.35)
      const trail4 = circleMask({
        coordsNDC: this.circleTrailPositions[4],
        radiusNDC: this.circleRadiusNDC,
      }).mul(0.3)
      const trail5 = circleMask({
        coordsNDC: this.circleTrailPositions[5],
        radiusNDC: this.circleRadiusNDC,
      }).mul(0.25)
      const trail6 = circleMask({
        coordsNDC: this.circleTrailPositions[6],
        radiusNDC: this.circleRadiusNDC,
      }).mul(0.2)
      const trail7 = circleMask({
        coordsNDC: this.circleTrailPositions[7],
        radiusNDC: this.circleRadiusNDC,
      }).mul(0.15)
      const trail8 = circleMask({
        coordsNDC: this.circleTrailPositions[8],
        radiusNDC: this.circleRadiusNDC,
      }).mul(0.1)
      const trail9 = circleMask({
        coordsNDC: this.circleTrailPositions[9],
        radiusNDC: this.circleRadiusNDC,
      }).mul(0.05)

      const trailColor = vec3(1).mul(
        trail0
          .add(trail1)
          .add(trail2)
          .add(trail3)
          .add(trail4)
          .add(trail5)
          .add(trail6)
          .add(trail7)
          .add(trail8)
          .add(trail9),
      )

      const circleColor = vec3(1).mul(currentCircleMask).add(trailColor)

      const plusPatternNode = plusPattern({
        color: this.plusPatternColor,
        spacing: this.plusPatternSpacing,
        size: this.plusPatternSize,
        visibility: this.plusPatternVisibility,
        blinkSpeed: this.plusPatternBlinkSpeed,
      })

      // Add plus pattern using alpha mask (additive blending)
      // Only calculate and apply when plusPatternEnabled is 1
      const plusColor = plusPatternNode.rgb
      const plusAlpha = plusPatternNode.a
      const plusPatternColor = plusColor
        .mul(plusAlpha)
        .pow(2)
        .mul(0.3)
        .mul(this.plusPatternEnabled)

      // Grid pattern
      const gridPatternNode = gridPattern({
        color: this.gridColor,
        size: this.gridSize.div(100),
      })
      const gridPatternColor = gridPatternNode.rgb.mul(gridPatternNode.a)

      // Calculate distance from circle center for threshold effect
      const screenDist = length(fragScreen.sub(this.mouseCoordsNDC))
      const halfRadius = this.circleRadiusNDC.mul(0.8)

      // Create threshold: 0 until half radius, then exponential bump
      const distanceFromHalf = screenDist.sub(halfRadius)
      const thresholdMask = step(distanceFromHalf, float(0)) // 1 when inside half radius

      // Exponential curve: very steep bump after threshold
      // Use inverted distance (closer to center = higher value)
      const normalizedDist = distanceFromHalf.div(halfRadius).mul(-1) // Invert and normalize
      const expCurve = pow(max(float(0), normalizedDist), float(8)) // Very steep exponential

      // Apply threshold and exponential curve
      const gridPatternBoosted = gridPatternColor
        .mul(thresholdMask)
        .mul(expCurve)
        .mul(float(0.2)) // Boost intensity
        .mul(circleColor)
        .mul(this.fadeOutMultiplier)

      const finalColor = frColor
        .add(gridPatternBoosted)
        .add(this.shadowColor.mul(circleColor).mul(this.fadeOutMultiplier))
        .add(plusPatternColor.mul(circleColor).mul(this.fadeOutMultiplier))

      return finalColor
    })

    material.colorNode = colorShader()

    this.model.traverse((child) => {
      if (child instanceof THREE.SkinnedMesh) {
        child.material = material
      }
    })
  }

  startIdlePosition() {
    this.animatedModel?.startIdleAction()
  }

  listenEvents() {
    useBrowserLocation.subscribe((state, prevState) => {
      if (state.pathname === "/about") {
        // Clear any existing timeouts before setting new ones
        if (this.aboutPageTimeoutId) {
          clearTimeout(this.aboutPageTimeoutId)
          this.aboutPageTimeoutId = null
        }
        if (this.aboutPageIdleTimeoutId) {
          clearTimeout(this.aboutPageIdleTimeoutId)
          this.aboutPageIdleTimeoutId = null
        }

        this.aboutPageTimeoutId = setTimeout(() => {
          this.cleanupHeadUpAnimations()
          const introAction = this.animatedModel.findAction("intro")
          const idleAction = this.animatedModel.findAction("idle")
          const activeAction = this.animatedModel.getActiveAction()

          assert(introAction, "Intro action not found")
          assert(idleAction, "Idle action not found")

          // Ensure idle is playing in background to preserve its time
          if (!idleAction.isRunning()) {
            idleAction.play()
          }

          // Ensure idle is considered active for fadeToAction
          // Temporarily restore its weight if needed, then use fadeToAction
          const idleWasActive = activeAction === idleAction
          if (!idleWasActive && idleAction.getEffectiveWeight() === 0) {
            // Idle needs to be active for fadeToAction to work
            // Temporarily set it to a low weight
            idleAction.setEffectiveWeight(0.01)
          }

          // Use fadeToAction to properly initialize and start intro
          // This ensures intro is reset, playing, and will animate to the intro pose
          this.animatedModel.fadeToAction("intro", 0.3)

          // After fadeToAction completes, set idle weight to 0 to keep it playing in background
          this.aboutPageIdleTimeoutId = setTimeout(() => {
            idleAction.setEffectiveWeight(0)
            this.aboutPageIdleTimeoutId = null
          }, 350)

          this.animatedModel.lastActivatedAction = introAction
          this.aboutPageTimeoutId = null
        }, 300)
      }

      if (prevState.pathname === "/about" && state.pathname === "/") {
        // Clear timeouts when navigating away from /about
        if (this.aboutPageTimeoutId) {
          clearTimeout(this.aboutPageTimeoutId)
          this.aboutPageTimeoutId = null
        }
        if (this.aboutPageIdleTimeoutId) {
          clearTimeout(this.aboutPageIdleTimeoutId)
          this.aboutPageIdleTimeoutId = null
        }
        const introAction = this.animatedModel.findAction("intro")
        const idleAction = this.animatedModel.findAction("idle")
        const activeAction = this.animatedModel.getActiveAction()

        assert(idleAction, "Idle action not found")

        // Ensure intro is active for fadeToAction to work
        // fadeToAction requires a previous active action
        if (!activeAction || activeAction !== introAction) {
          // If intro is not active, make it active first
          if (introAction && !introAction.isRunning()) {
            introAction.reset()
            introAction.setEffectiveTimeScale(1)
            introAction.setEffectiveWeight(1)
            introAction.play()
            this.animatedModel.lastActivatedAction = introAction
          }
        }

        // Use fadeToAction to properly initialize and start idle
        // This ensures idle is reset, started, and will animate properly
        // fadeToAction will fade out intro and fade in idle
        this.animatedModel.fadeToAction("idle", 1)
      }
    })

    useLoadingStore.subscribe((state, prevState) => {
      assert(this.animatedModel, "AnimatedModel is not initialized")

      //
      // HOVER
      if (state.hasEnterHover && !prevState.hasEnterHover) {
        this.headUpHandler(true)
      }
      if (
        !state.hasEnterHover &&
        prevState.hasEnterHover &&
        window.location.pathname !== "/about"
      ) {
        this.headUpHandler(false)
      }
    })
  }

  cleanupHeadUpAnimations() {
    assert(this.animatedModel.mixer, "Animation Mixer is not initialized")

    // Remove event listener first to prevent head-up-loop from starting
    if (this.headUpLoopListenerBound) {
      this.animatedModel.mixer.removeEventListener(
        "finished",
        this.headUpLoopListenerBound,
      )
    }

    // Stop and reset head-up-loop if it's playing
    const headUpLoopAction = this.animatedModel.findAction("head-up-loop")
    if (headUpLoopAction && headUpLoopAction.isRunning()) {
      headUpLoopAction.fadeOut(0.3)
    }

    // Stop and reset head-up action
    const headUpAction = this.animatedModel.findAction("head-up")
    if (headUpAction && headUpAction.isRunning()) {
      headUpAction.fadeOut(0.3)
    }
  }

  headUpHandler(isHover: boolean) {
    assert(this.animatedModel.mixer, "Animation Mixer is not initialized")

    if (isHover) {
      const headUpAction = this.animatedModel.findAction("head-up")
      assert(headUpAction, "Head up action not found")

      headUpAction.loop = THREE.LoopOnce
      headUpAction.clampWhenFinished = true // animation will be paused at the end

      this.animatedModel.fadeToAction("head-up", 0.3)

      // Store bound listener to use same reference for removal
      if (!this.headUpLoopListenerBound) {
        this.headUpLoopListenerBound = this.headUpLoopListener.bind(this)
      }

      this.animatedModel.mixer.addEventListener(
        "finished",
        this.headUpLoopListenerBound,
      )
    } else {
      this.cleanupHeadUpAnimations()

      // Ensure intro animation is fully stopped before fading to idle
      const introAction = this.animatedModel.findAction("intro")
      if (
        introAction &&
        introAction.isRunning() &&
        introAction.getEffectiveWeight() > 0
      ) {
        // Fade out intro first to prevent state conflicts
        introAction.fadeOut(0.3)
      }

      // Check if idle is already the active action to avoid unnecessary reset
      const activeAction = this.animatedModel.getActiveAction()
      const idleAction = this.animatedModel.findAction("idle")

      if (
        activeAction === idleAction &&
        idleAction &&
        idleAction.getEffectiveWeight() > 0.9
      ) {
        // Idle is already active with high weight, just ensure it's playing
        if (!idleAction.isRunning()) {
          idleAction.play()
        }
      } else {
        // Fade to idle
        this.animatedModel.fadeToAction("idle", 0.7)
      }
    }
  }

  headUpLoopListener(event: THREE.AnimationMixerEventMap["finished"]) {
    const clip = event.action.getClip()
    if (clip.name === "head-up") {
      event.action.loop = THREE.LoopRepeat
      event.action.clampWhenFinished = false
      this.animatedModel.fadeToAction("head-up-loop", 0.3)
    }
  }

  setDebug() {
    if (!this.debugFolder) return

    const folder = this.debugFolder.addFolder({
      title: "👤 Man",
      expanded: false,
    })

    folder
      .addBinding(this.settings.position, "z", {
        min: -10,
        max: 10,
        step: 0.1,
      })
      .on("change", (ev) => {
        if (this.model) {
          this.model.position.z = ev.value
        }
      })

    folder
      .addBinding(this.settings.position, "x", {
        min: -10,
        max: 10,
        step: 0.1,
      })
      .on("change", (ev) => {
        if (this.model) {
          this.model.position.x = ev.value
        }
      })

    folder
      .addBinding(this.settings.rotation, "y", {
        label: "Rotation Y",
        min: -Math.PI * 2,
        max: Math.PI * 2,
        step: 0.01,
      })
      .on("change", (ev) => {
        if (this.model) {
          this.model.rotation.y = ev.value
        }
      })

    const uniforms = {
      fresnelMultiplier: this.settings.light.fresnelMultiplier.value,
      fresnelFadeHeight: this.settings.light.fresnelFadeHeight.value,
    }

    folder
      .addBinding(uniforms, "fresnelMultiplier", {
        min: 0,
        max: 1,
        step: 0.01,
      })
      .on("change", (ev) => {
        this.settings.light.fresnelMultiplier.value = ev.value
      })

    folder
      .addBinding(uniforms, "fresnelFadeHeight", {
        label: "Fresnel Fade Height",
        min: 0,
        max: 2,
        step: 0.01,
      })
      .on("change", (ev) => {
        this.settings.light.fresnelFadeHeight.value = ev.value
      })

    const plusPatternFolder = folder.addFolder({
      title: "Plus Pattern",
    })

    // Color control
    const colorSettings = {
      color: `#${new THREE.Color(this.plusPatternColor.value).getHexString()}`,
    }

    plusPatternFolder
      .addBinding(colorSettings, "color", {
        view: "color",
      })
      .on("change", (ev) => {
        this.plusPatternColor.value = new THREE.Color(ev.value)
      })

    // Spacing control
    const spacingSettings = {
      spacing: this.plusPatternSpacing.value,
    }

    plusPatternFolder
      .addBinding(spacingSettings, "spacing", {
        min: 0.001,
        max: 0.01,
        step: 0.001,
      })
      .on("change", (ev) => {
        this.plusPatternSpacing.value = ev.value
      })

    // Size control
    const sizeSettings = {
      size: this.plusPatternSize.value,
    }

    plusPatternFolder
      .addBinding(sizeSettings, "size", {
        min: 0.001,
        max: 0.5,
        step: 0.001,
      })
      .on("change", (ev) => {
        this.plusPatternSize.value = ev.value
      })

    // Visibility control
    const visibilitySettings = {
      visibility: this.plusPatternVisibility.value,
    }

    plusPatternFolder
      .addBinding(visibilitySettings, "visibility", {
        min: 0,
        max: 1,
        step: 0.01,
        label: "visibility (%)",
      })
      .on("change", (ev) => {
        this.plusPatternVisibility.value = ev.value
      })

    // Blink speed control
    const blinkSpeedSettings = {
      blinkSpeed: this.plusPatternBlinkSpeed.value,
    }

    plusPatternFolder
      .addBinding(blinkSpeedSettings, "blinkSpeed", {
        min: 0,
        max: 10,
        step: 0.1,
        label: "blink speed",
      })
      .on("change", (ev) => {
        this.plusPatternBlinkSpeed.value = ev.value
      })

    // Circle trail control
    const circleTrailFolder = folder.addFolder({
      title: "Circle Trail",
    })

    const trailSettings = {
      updateInterval: this.trailUpdateInterval,
    }

    circleTrailFolder
      .addBinding(trailSettings, "updateInterval", {
        min: 1,
        max: 10,
        step: 1,
        label: "persistence (frames)",
      })
      .on("change", (ev) => {
        this.trailUpdateInterval = ev.value
      })

    // Shadow color control
    const shadowColorSettings = {
      color: `#${new THREE.Color(this.shadowColor.value).getHexString()}`,
    }

    circleTrailFolder
      .addBinding(shadowColorSettings, "color", {
        view: "color",
      })
      .on("change", (ev) => {
        this.shadowColor.value = new THREE.Color(ev.value)
      })

    // Grid pattern control
    const gridFolder = folder.addFolder({
      title: "Grid Pattern",
    })

    // Grid color control
    const gridColorSettings = {
      color: `#${new THREE.Color(this.gridColor.value).getHexString()}`,
    }

    gridFolder
      .addBinding(gridColorSettings, "color", {
        view: "color",
      })
      .on("change", (ev) => {
        this.gridColor.value = new THREE.Color(ev.value)
      })

    // Grid size control
    const gridSizeSettings = {
      size: this.gridSize.value,
    }

    gridFolder
      .addBinding(gridSizeSettings, "size", {
        min: 0.01,
        max: 2.0,
        step: 0.01,
        label: "square size",
      })
      .on("change", (ev) => {
        this.gridSize.value = ev.value
      })

    const shadowFolder = folder.addFolder({
      title: "Fake Shadow",
    })

    const shadowSettings = {
      radius: this.shadowSettings.radius,
      opacity: this.shadowSettings.opacity,
      yOffset: this.shadowSettings.yOffset,
    }

    shadowFolder
      .addBinding(shadowSettings, "radius", {
        min: 0.1,
        max: 10,
        step: 0.01,
      })
      .on("change", (ev) => {
        this.shadowSettings.radius = ev.value
        if (this.shadowMesh) this.shadowMesh.scale.set(ev.value, ev.value, 1)
      })

    shadowFolder
      .addBinding(shadowSettings, "opacity", {
        min: 0,
        max: 1,
        step: 0.01,
      })
      .on("change", (ev) => {
        this.shadowSettings.opacity = ev.value
        this.shadowOpacity.value = ev.value
      })

    shadowFolder
      .addBinding(shadowSettings, "yOffset", {
        min: 0,
        max: 0.05,
        step: 0.001,
      })
      .on("change", (ev) => {
        this.shadowSettings.yOffset = ev.value
        if (this.shadowMesh) this.shadowMesh.position.y = ev.value
      })
  }

  update({ delta }: { delta: number }) {
    this.animatedModel?.update({ delta })
    this.outerParticles?.update()

    if (this.shadowMesh) {
      this.shadowMesh.position.x = this.model.position.x - 0.3
      this.shadowMesh.position.z = this.model.position.z
      this.shadowMesh.position.y = this.shadowSettings.yOffset
    }

    // Handle fade out when mouse leaves
    if (!this.isMouseOver) {
      // Fade out over time
      this.fadeOutMultiplier.value = Math.max(
        0,
        this.fadeOutMultiplier.value - 0.05,
      )
      this.circleActive.value = this.fadeOutMultiplier.value
    } else {
      // Fade in when mouse enters
      this.fadeOutMultiplier.value = Math.min(
        1,
        this.fadeOutMultiplier.value + 0.1,
      )
      this.circleActive.value = this.fadeOutMultiplier.value
    }

    if (this.fadeOutMultiplier.value <= 0 && !this.isMouseOver) {
      return
    }

    // Update trail less frequently to make it persist longer
    this.trailUpdateCounter++
    if (this.trailUpdateCounter >= this.trailUpdateInterval) {
      // Update trail: shift positions and add current position
      for (let i = this.circleTrailLength - 1; i > 0; i--) {
        this.circleTrailPositions[i].value.copy(
          this.circleTrailPositions[i - 1].value,
        )
      }
      this.circleTrailPositions[0].value.set(
        this.pointer.ndcX,
        this.pointer.ndcY,
      )
      this.trailUpdateCounter = 0
    }

    // Update mouse circle uniform in NDC space (always update current position)
    this.mouseCoordsNDC.value.x = this.pointer.ndcX
    this.mouseCoordsNDC.value.y = this.pointer.ndcY
    this.circleActive.value = 1
  }
}
